// Bank Balance Entry (from [Reporting].BankBalanceEntries)
export interface BankBalanceEntry {
    EntryID: number;
    CompanyCode: string;
    BankID: string;
    PostingDate: string;
    CurrencyCode: string;
    AmountFCY: number;
    Notes: string | null;
    IsAutomatic: boolean;
    PostingYear: number;
    PostingMonth: number;
    PostingDay: number;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
    // Joined from BankAccountConfig
    BankAccountName: string | null;
    BankCompanyName: string | null;
    AccountType: string | null;
}

// Bank Account (from [Reporting].BankAccountConfig, used for dropdowns)
// BankAccountCode is null for manual-country accounts with no ERP (etl.BankAccount) row.
export interface BankAccount {
    BankID: string;
    BankAccountCode: string | null;
    BankAccountName: string;
    CurrencyCode: string;
    CompanyCode: string;
    BankCompanyName: string | null;
    AccountType: string | null;
    IsCashRelevant: boolean | null;
    IsActive: boolean | null;
}

// Bank Account Config (from [Reporting].BankAccountConfig)
export interface BankAccountConfig {
    Id: number;
    CompanyCode: string;
    BankID: string;
    BankAccountName: string;
    BankCompanyName: string;
    AccountType: string;
    DataSource: string;
    IsCashRelevant: boolean;
    IsActive: boolean;
    CreditLimit: number | null;
    InterestRate: number | null;
    MaturityDate: string | null;
    RenewalType: string | null;
    SortOrder: number;
    Notes: string | null;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
    CurrencyCode: string | null;
    IBAN: string | null;
}

// Currency Summary
export interface CurrencySummary {
    CurrencyCode: string;
    TotalAmount: number;
    AccountCount: number;
}

// Comparison data (previous month per bank)
export interface BankComparison {
    BankID: string;
    PrevAmountFCY: number;
    PrevDate: string;
}

// Trend data
export interface TrendDataPoint {
    PostingYear: number;
    PostingMonth: number;
    CurrencyCode: string;
    TotalAmount: number;
    AccountCount: number;
}

export interface TypeBreakdown {
    AccountType: string;
    TotalAmount: number;
    AccountCount: number;
    CurrencyCode: string;
}

export interface TrendData {
    trend: TrendDataPoint[];
    byType: TypeBreakdown[];
}

// Sync check result
export interface SyncCheckResult {
    unclassifiedCount: number;
    unclassified: {
        BankID: string;
        BankAccountName: string;
        CompanyCode: string;
        CurrencyCode: string;
        DataSource: string;
    }[];
}

// Create/update body types
export interface CreateEntryData {
    companyCode: string;
    bankId: string;
    postingDate: string;
    currencyCode: string;
    amountFCY: number;
    notes?: string | null;
}

export interface UpdateEntryData {
    bankId?: string;
    postingDate?: string;
    currencyCode?: string;
    amountFCY?: number;
    notes?: string | null;
}

export interface CopyEntriesData {
    companyCode: string;
    sourceDate: string;
    targetDate: string;
}

export interface CreateBankConfigData {
    companyCode: string;
    bankId: string;
    bankAccountName: string;
    bankCompanyName: string;
    accountType?: string;
    dataSource?: string;
    isCashRelevant?: boolean;
    isActive?: boolean;
    creditLimit?: number | null;
    interestRate?: number | null;
    maturityDate?: string | null;
    renewalType?: string | null;
    sortOrder?: number;
    notes?: string | null;
}

export interface UpdateBankConfigData {
    bankAccountName?: string;
    bankCompanyName?: string;
    accountType?: string;
    dataSource?: string;
    isCashRelevant?: boolean;
    isActive?: boolean;
    creditLimit?: number | null;
    interestRate?: number | null;
    maturityDate?: string | null;
    renewalType?: string | null;
    sortOrder?: number;
    notes?: string | null;
}

export const ACCOUNT_TYPES = ['Account', 'CallDeposit', 'TimeDeposit', 'FixedTermLoan', 'CreditLine', 'IntercompanyLoan'] as const;
export type AccountType = typeof ACCOUNT_TYPES[number];

export const ACCOUNT_TYPE_LABELS: Record<string, string> = {
    Account: 'Bank Account',
    CallDeposit: 'Call Deposit',
    TimeDeposit: 'Time Deposit',
    FixedTermLoan: 'Fixed Term Loan',
    CreditLine: 'Credit Line',
    IntercompanyLoan: 'Intercompany Loan',
};

export const DATA_SOURCES = ['Manual', 'EOS', 'RED'] as const;
export const RENEWAL_TYPES = ['Fixed', 'Rolling', 'None'] as const;

// ----- Weekly AR/AP Balance (Non-BC companies) -----

export const NON_BC_COMPANIES = ['dsp', 'dtr', 'dfr', 'dpt', 'dde', 'dat'] as const;
export type NonBcCompany = typeof NON_BC_COMPANIES[number];

/** Default local currency per non-BC company (derived from BankBalanceEntries usage). */
export const NON_BC_DEFAULT_CURRENCY: Record<string, string> = {
    dsp: 'EUR',
    dtr: 'TRY',
    dfr: 'EUR',
    dpt: 'EUR',
    dde: 'EUR',
    dat: 'EUR',
};

export function isNonBcCompany(code: string | null | undefined): boolean {
    return !!code && (NON_BC_COMPANIES as readonly string[]).includes(code);
}

export interface WeeklyArApBalance {
    Id: number;
    CompanyCode: string;
    PostingDate: string;
    CurrencyCode: string;
    ArAmountLCY: number;
    ApAmountLCY: number;
    Comment: string | null;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
}

export interface CreateWeeklyArApData {
    companyCode: string;
    postingDate: string;
    currencyCode: string;
    arAmountLCY: number;
    apAmountLCY: number;
    comment?: string | null;
}

export interface UpdateWeeklyArApData {
    currencyCode?: string;
    arAmountLCY?: number;
    apAmountLCY?: number;
    comment?: string | null;
}

/** One active account on the quick-entry page (/bank-balance/weekly). */
export interface WeeklyAccount {
    BankID: string;
    BankAccountName: string;
    CurrencyCode: string | null;
    CurrentEntryID: number | null;
    CurrentAmount: number | null;
    CurrentIsAutomatic: boolean | null;
    LastDate: string | null;
    LastAmount: number | null;
}

export interface WeeklyBalances {
    companyCode: string;
    date: string;
    today: string;
    accounts: WeeklyAccount[];
}

export interface SaveWeeklyData {
    postingDate: string;
    companyCode?: string;
    items: { bankId: string; amountFCY: number }[];
}
